import { createAction, props } from '@ngrx/store';
import { getActionName } from '@app/core/state/state-helpers';
import { PagedApiData } from '@app/core/models/PagedApiData';
import { WhatsappMessageHistory } from '@app/communications/models/responses/whatsapp-message-history';
import { UserMessagingMetadata } from '@app/communications/models/responses/user-messaging-metadata';
import { SortPropDir } from '@swimlane/ngx-datatable';
import {
  ComplexSearchRequest,
  SearchRequest,
} from '@app/shared/search/interfaces/search-request';
import { MessageTypeEnum } from '@app/communications/models/enums/message-type.enum';
import { MessageHistory } from '@app/communications/models/responses/message-history';
import { WhatsappConversationDetails } from '@app/communications/models/responses/whatsapp-conversation-details';
import { Message } from '@app/communications/models/responses/message';
import { SendWhatsAppMessageRequest } from '@app/communications/models/requests/send-whatsapp-message-request';
import { SendClaimMessageRequest } from '@app/communications/models/requests/send-claim-message-request';
import { ClaimContactDetails } from '@app/communications/models/responses/claim-contact-details';
import { AllowedWhatsAppFileTypes } from '@app/communications/models/responses/allowed-whatsapp-file-types';
import { TemplatedMessage } from '@app/communications/models/responses/templated-message';
import { WhatsappTemplate } from '@app/communications/models/enums/whatsapp-template.enum';
import { SystemContact } from '@app/communications/models/responses/system-contact';
import { SendPensionsBeneficiaryMessageRequest } from '@app/communications/models/requests/send-pensions-beneficiary-message-request';
import { PensionsBeneficiaryContactDetails } from '@app/communications/models/responses/pensions-beneficiary-contact-details';
import { SendSmsRequest } from '@app/communications/models/requests/send-sms-request';
import { EducationAssistanceBeneficiaryContactDetails } from '@app/communications/models/responses/education-assistance-beneficiary-contact-details';
import { SendEducationAssistanceMessageRequest } from '@app/communications/models/requests/send-education-assistance-message-request';
import { UnreadWhatsappConversationsNotification } from '@app/communications/models/responses/unread-whatsapp-conversations.notification';
import { GenericTableActionsWithoutPropClass } from '@app/shared/table/helpers/generic-table-actions';
import { CommunicationsState } from '@app/communications/state/communications.state';
import { CommunicationTables } from '@app/communications/models/enums/communication-tables.enum';
import { getCommunicationsFeatureState } from '@app/communications/state/communications.selectors';
import { CustomClaimSmsResponse } from '@app/communications/models/responses/custom-claim-sms-response';
import { StartClaimConversationResponse } from '@app/communications/models/responses/start-claim-conversation-response';
import { ActionCustomClaimSms } from '@app/communications/models/requests/action-custom-claim-sms';
import { PendingCustomClaimSms } from '@app/communications/models/responses/pending-custom-claim-sms';

export const communicationsActionPrefix = '[Communications]';

export const loadUserMessagingMetadata = createAction(
  getActionName('Load user messaging metadata', communicationsActionPrefix),
);

export const loadUserMessagingMetadataSuccess = createAction(
  getActionName(
    'Load user messaging metadata success',
    communicationsActionPrefix,
  ),
  props<{ response: UserMessagingMetadata }>(),
);

export const loadUserMessagingMetadataFailure = createAction(
  getActionName(
    'Load user messaging metadata failure',
    communicationsActionPrefix,
  ),
  props<{ error: string }>(),
);
export const loadUserMessageHistory = createAction(
  getActionName('Load user message history', communicationsActionPrefix),
  props<{
    metadata: UserMessagingMetadata;
    limit: number;
    offset: number;
    searchData: SearchRequest;
    complexSearchData: ComplexSearchRequest;
    sort: SortPropDir;
  }>(),
);

export const loadUserMessageHistorySuccess = createAction(
  getActionName(
    'Load user message history success',
    communicationsActionPrefix,
  ),
  props<{ response: PagedApiData<WhatsappMessageHistory> }>(),
);

export const loadUserMessageHistoryFailure = createAction(
  getActionName(
    'Load user message history failure',
    communicationsActionPrefix,
  ),
  props<{ error: string }>(),
);

export const clearUserMessageHistory = createAction(
  getActionName('Clear user message history', communicationsActionPrefix),
);

export const loadConversationDetails = createAction(
  getActionName('Load conversation details', communicationsActionPrefix),
  props<{ conversationId: number }>(),
);

export const loadConversationDetailsSuccess = createAction(
  getActionName(
    'Load conversation details success',
    communicationsActionPrefix,
  ),
  props<{ conversation: WhatsappConversationDetails }>(),
);

export const loadConversationDetailsFailure = createAction(
  getActionName(
    'Load conversation details failure',
    communicationsActionPrefix,
  ),
  props<{ error: string }>(),
);

export const loadMessageHistory = createAction(
  getActionName('Load message history', communicationsActionPrefix),
  props<{
    messageType: MessageTypeEnum[];
    senders: string[];
    receivers: string[];
    limit: number;
    offset: number;
    searchData: SearchRequest;
    complexSearchData: ComplexSearchRequest;
    sort: SortPropDir;
  }>(),
);

export const loadMessageHistorySuccess = createAction(
  getActionName('Load message history success', communicationsActionPrefix),
  props<{ response: PagedApiData<MessageHistory> }>(),
);

export const loadMessageHistoryFailure = createAction(
  getActionName('Load message history failure', communicationsActionPrefix),
  props<{ error: string }>(),
);

export const clearMessageHistory = createAction(
  getActionName('Clear message history', communicationsActionPrefix),
);

export const loadWhatsappConversationMessages = createAction(
  getActionName(
    'Load WhatsApp conversation messages',
    communicationsActionPrefix,
  ),
  props<{ conversationId: number; limit: number; offset: number }>(),
);

export const loadWhatsappConversationMessagesSuccess = createAction(
  getActionName(
    'Load WhatsApp conversation messages success',
    communicationsActionPrefix,
  ),
  props<{ conversationId: number; response: PagedApiData<Message> }>(),
);

export const loadWhatsappConversationMessagesFailure = createAction(
  getActionName(
    'Load WhatsApp conversation messages failure',
    communicationsActionPrefix,
  ),
  props<{ error: string }>(),
);

export const clearWhatsappConversationMessages = createAction(
  getActionName(
    'Clear WhatsApp conversation messages',
    communicationsActionPrefix,
  ),
);

export const loadClaimSmsMessageTemplatePreview = createAction(
  getActionName(
    'Load Claim Sms message template preview',
    communicationsActionPrefix,
  ),
  props<{ messageTemplate: MessageTypeEnum; claimCode: string }>(),
);

export const loadClaimSmsMessageTemplatePreviewSuccess = createAction(
  getActionName(
    'Load Claim Sms message template preview  success',
    communicationsActionPrefix,
  ),
  props<{ template: string }>(),
);

export const loadClaimSmsMessageTemplatePreviewFailure = createAction(
  getActionName(
    'Load Claim Sms message template preview failure',
    communicationsActionPrefix,
  ),
  props<{ error: string }>(),
);

export const clearClaimSmsMessageTemplatePreview = createAction(
  getActionName(
    'Clear Claim Sms message template preview',
    communicationsActionPrefix,
  ),
);

export const markWhatsappConversationAsRead = createAction(
  getActionName(
    'Mark WhatsApp conversation as read',
    communicationsActionPrefix,
  ),
  props<{ conversationId: number }>(),
);

export const markWhatsappConversationAsReadSuccess = createAction(
  getActionName(
    'Mark WhatsApp conversation as read success',
    communicationsActionPrefix,
  ),
  props<{ conversationId: number }>(),
);

export const markWhatsappConversationAsReadFailure = createAction(
  getActionName(
    'Mark WhatsApp conversation as read failure',
    communicationsActionPrefix,
  ),
  props<{ error: string }>(),
);
export const sendWhatsAppMessage = createAction(
  getActionName('Send WhatsApp message', communicationsActionPrefix),
  props<{ request: SendWhatsAppMessageRequest }>(),
);

export const sendWhatsAppMessageSuccess = createAction(
  getActionName('Send WhatsApp message success', communicationsActionPrefix),
  props<{ message: Message }>(),
);

export const sendWhatsAppMessageFailure = createAction(
  getActionName('Send WhatsApp message failure', communicationsActionPrefix),
  props<{ error: string }>(),
);

export const loadLatestMessagesForConversation = createAction(
  getActionName(
    'Load latest messages for conversation',
    communicationsActionPrefix,
  ),
  props<{ conversationId: number }>(),
);

export const loadLatestMessagesForConversationSuccess = createAction(
  getActionName(
    'Load latest messages for conversation success',
    communicationsActionPrefix,
  ),
  props<{ conversationId: number; messages: Message[] }>(),
);

export const loadLatestMessagesForConversationFailure = createAction(
  getActionName(
    'Load latest messages for conversation failure',
    communicationsActionPrefix,
  ),
  props<{ error: string }>(),
);

export const loadClaimContactDetails = createAction(
  getActionName(
    'Load claim contact detail form detail',
    communicationsActionPrefix,
  ),
  props<{ claimCode: string }>(),
);
export const loadClaimContactDetailsDataSuccess = createAction(
  getActionName(
    'Load claim contact detail form detail success',
    communicationsActionPrefix,
  ),
  props<{ response: ClaimContactDetails[] }>(),
);
export const loadClaimContactDetailsDataFailure = createAction(
  getActionName(
    'Load claim contact detail form detail failure',
    communicationsActionPrefix,
  ),
  props<{ error: string }>(),
);
export const startClaimWhatsAppConversation = createAction(
  getActionName(
    'Start claim WhatsApp conversations',
    communicationsActionPrefix,
  ),
  props<{ request: SendClaimMessageRequest }>(),
);
export const startClaimWhatsAppConversationSuccess = createAction(
  getActionName(
    'Start claim WhatsApp conversations success',
    communicationsActionPrefix,
  ),
);
export const startClaimWhatsAppConversationFailure = createAction(
  getActionName(
    'Start claim WhatsApp conversations failure',
    communicationsActionPrefix,
  ),
  props<{ error: string }>(),
);

export const startClaimSmsConversation = createAction(
  getActionName('Start claim Sms conversations', communicationsActionPrefix),
  props<{
    request: SendClaimMessageRequest;
    callBack: (response: StartClaimConversationResponse) => void;
  }>(),
);
export const startClaimSmsConversationSuccess = createAction(
  getActionName(
    'Start claim Sms conversations success',
    communicationsActionPrefix,
  ),
);
export const startClaimSmsConversationFailure = createAction(
  getActionName(
    'Start claim Sms conversations failure',
    communicationsActionPrefix,
  ),
  props<{ error: string }>(),
);

export const sendWhatsAppOptIn = createAction(
  getActionName('Send WhatsApp Opt In', communicationsActionPrefix),
  props<{ request: SendWhatsAppMessageRequest }>(),
);
export const sendWhatsAppOptInSuccess = createAction(
  getActionName('Send WhatsApp Opt In success', communicationsActionPrefix),
);
export const sendWhatsAppOptInFailure = createAction(
  getActionName('Send WhatsApp Opt In failure', communicationsActionPrefix),
  props<{ error: string }>(),
);

export const sendSms = createAction(
  getActionName('Send sms', communicationsActionPrefix),
  props<{ request: SendSmsRequest }>(),
);

export const sendSmsSuccess = createAction(
  getActionName('Send sms success', communicationsActionPrefix),
);

export const sendSmsFailure = createAction(
  getActionName('Send sms failure', communicationsActionPrefix),
  props<{ error: string }>(),
);
export const downloadMediaFile = createAction(
  getActionName('Download media file', communicationsActionPrefix),
  props<{ id: number }>(),
);
export const downloadMediaFileSuccess = createAction(
  getActionName('Download media file success', communicationsActionPrefix),
  props<{ id: number }>(),
);
export const downloadMediaFileFailure = createAction(
  getActionName('Download media file failure', communicationsActionPrefix),
  props<{ error: string; id: number }>(),
);

export const getWhatsAppAllowedFileTypes = createAction(
  getActionName('Get WhatsApp allowed file types', communicationsActionPrefix),
);

export const getWhatsAppAllowedFileTypesSuccess = createAction(
  getActionName(
    'Get WhatsApp allowed file types success',
    communicationsActionPrefix,
  ),
  props<{ response: AllowedWhatsAppFileTypes }>(),
);

export const getWhatsAppAllowedFileTypesFailure = createAction(
  getActionName(
    'Get WhatsApp allowed file types failure',
    communicationsActionPrefix,
  ),
  props<{ error: string }>(),
);

export const getTemplatedMessage = createAction(
  getActionName('Get templated message', communicationsActionPrefix),
  props<{ template: WhatsappTemplate; contactId: number }>(),
);

export const getTemplatedMessageSuccess = createAction(
  getActionName('Get templated message success', communicationsActionPrefix),
  props<{ response: TemplatedMessage }>(),
);

export const getTemplatedMessageFailure = createAction(
  getActionName('Get templated message failure', communicationsActionPrefix),
  props<{ error: string }>(),
);

export const resetTemplatedMessage = createAction(
  getActionName('Reset templated message', communicationsActionPrefix),
);

export const sendTemplateMessage = createAction(
  getActionName('Send template message', communicationsActionPrefix),
  props<{ conversationId: number; templateId: WhatsappTemplate }>(),
);

export const sendTemplateMessageSuccess = createAction(
  getActionName('Send template message success', communicationsActionPrefix),
  props<{ conversationId: number }>(),
);

export const sendTemplateMessageFailure = createAction(
  getActionName('Send template message failure', communicationsActionPrefix),
  props<{ error: string }>(),
);

export const loadSystemContactDetails = createAction(
  getActionName('Load system contact-details', communicationsActionPrefix),
);

export const loadSystemContactDetailsSuccess = createAction(
  getActionName(
    'Load system contact-details success',
    communicationsActionPrefix,
  ),
  props<{ response: SystemContact[] }>(),
);

export const loadSystemContactDetailsFailure = createAction(
  getActionName(
    'Load system contact-details failure',
    communicationsActionPrefix,
  ),
  props<{ error: string }>(),
);

export const startPensionsBeneficiaryWhatsAppConversation = createAction(
  getActionName(
    'Start pensions beneficiary WhatsApp conversations',
    communicationsActionPrefix,
  ),
  props<{ request: SendPensionsBeneficiaryMessageRequest }>(),
);
export const startPensionsBeneficiaryWhatsAppConversationSuccess = createAction(
  getActionName(
    'Start pensions beneficiary WhatsApp conversations success',
    communicationsActionPrefix,
  ),
);
export const startPensionsBeneficiaryWhatsAppConversationFailure = createAction(
  getActionName(
    'Start pensions beneficiary WhatsApp conversations failure',
    communicationsActionPrefix,
  ),
  props<{ error: string }>(),
);

export const startPensionsBeneficiarySmsConversation = createAction(
  getActionName(
    'Start pensions beneficiary Sms conversations',
    communicationsActionPrefix,
  ),
  props<{ request: SendPensionsBeneficiaryMessageRequest }>(),
);
export const startPensionsBeneficiarySmsConversationSuccess = createAction(
  getActionName(
    'Start pensions beneficiary Sms conversations success',
    communicationsActionPrefix,
  ),
);
export const startPensionsBeneficiarySmsConversationFailure = createAction(
  getActionName(
    'Start pensions beneficiary Sms conversations failure',
    communicationsActionPrefix,
  ),
  props<{ error: string }>(),
);
export const loadPensionsBeneficiaryContactDetails = createAction(
  getActionName(
    'Load pensions beneficiary contact detail detail',
    communicationsActionPrefix,
  ),
  props<{ claimId: number }>(),
);
export const loadPensionsBeneficiaryContactDetailsDataSuccess = createAction(
  getActionName(
    'Load pensions beneficiary contact detail success',
    communicationsActionPrefix,
  ),
  props<{ response: PensionsBeneficiaryContactDetails[] }>(),
);
export const loadPensionsBeneficiaryContactDetailsDataFailure = createAction(
  getActionName(
    'Load pensions beneficiary contact detail failure',
    communicationsActionPrefix,
  ),
  props<{ error: string }>(),
);
export const loadPensionsSmsMessageTemplatePreview = createAction(
  getActionName(
    'Load Pensions Sms message template preview',
    communicationsActionPrefix,
  ),
  props<{ messageTemplate: MessageTypeEnum; claimId: number }>(),
);

export const loadPensionsSmsMessageTemplatePreviewSuccess = createAction(
  getActionName(
    'Load Pensions Sms message template preview  success',
    communicationsActionPrefix,
  ),
  props<{ template: string }>(),
);

export const loadPensionsSmsMessageTemplatePreviewFailure = createAction(
  getActionName(
    'Load Pensions Sms message template preview failure',
    communicationsActionPrefix,
  ),
  props<{ error: string }>(),
);

export const clearPensionsSmsMessageTemplatePreview = createAction(
  getActionName(
    'Clear Pensions Sms message template preview',
    communicationsActionPrefix,
  ),
);

export const loadSMSMessageHistory = createAction(
  getActionName('Load sms message history', communicationsActionPrefix),
  props<{
    messageType: MessageTypeEnum[];
    senders: string[];
    receivers: string[];
    limit: number;
    offset: number;
    searchData: SearchRequest;
    complexSearchData: ComplexSearchRequest;
    sort: SortPropDir;
  }>(),
);

export const loadSMSMessageHistorySuccess = createAction(
  getActionName('Load sms message history success', communicationsActionPrefix),
  props<{ response: PagedApiData<MessageHistory> }>(),
);

export const loadSMSMessageHistoryFailure = createAction(
  getActionName('Load sms message history failure', communicationsActionPrefix),
  props<{ error: string }>(),
);

export const clearSMSMessageHistory = createAction(
  getActionName('Clear sms message history', communicationsActionPrefix),
);

export const loadEducationAssistanceContactDetails = createAction(
  getActionName(
    'Load Education Assistance contact details',
    communicationsActionPrefix,
  ),
  props<{ beneficiaryId: number }>(),
);
export const loadEducationAssistanceContactDetailsSuccess = createAction(
  getActionName(
    'Load Education Assistance beneficiary contact detail success',
    communicationsActionPrefix,
  ),
  props<{ response: EducationAssistanceBeneficiaryContactDetails[] }>(),
);
export const loadEducationAssistanceContactDetailsFailure = createAction(
  getActionName(
    'Load Education Assistance beneficiary contact detail failure',
    communicationsActionPrefix,
  ),
  props<{ error: string }>(),
);

export const startEducationAssistanceWhatsAppConversation = createAction(
  getActionName(
    'Start EAP beneficiary WhatsApp conversation',
    communicationsActionPrefix,
  ),
  props<{ request: SendEducationAssistanceMessageRequest }>(),
);
export const startEducationAssistanceWhatsAppConversationSuccess = createAction(
  getActionName(
    'Start EAP beneficiary WhatsApp conversation success',
    communicationsActionPrefix,
  ),
);
export const startEducationAssistanceWhatsAppConversationFailure = createAction(
  getActionName(
    'Start EAP beneficiary WhatsApp conversation failure',
    communicationsActionPrefix,
  ),
  props<{ error: string }>(),
);

export const startWhatsAppUnreadNotifications = createAction(
  getActionName(
    'Create WhatsApp unread notifications',
    communicationsActionPrefix,
  ),
);
export const startWhatsAppUnreadNotificationsSuccess = createAction(
  getActionName(
    'Create WhatsApp unread notifications success',
    communicationsActionPrefix,
  ),
);
export const startWhatsAppUnreadNotificationsFailure = createAction(
  getActionName(
    'Create WhatsApp unread notifications failure',
    communicationsActionPrefix,
  ),
  props<{ error: string }>(),
);
export const updateWhatsAppUnreadNotifications = createAction(
  getActionName(
    'Update WhatsApp unread notifications',
    communicationsActionPrefix,
  ),
  props<{ notification: UnreadWhatsappConversationsNotification }>(),
);
export const startCommunicationsNotifications = createAction(
  getActionName(
    'Start communications notifications',
    communicationsActionPrefix,
  ),
);
export const startCommunicationsNotificationsSuccess = createAction(
  getActionName(
    'Start communications notifications success',
    communicationsActionPrefix,
  ),
);
export const startCommunicationsNotificationsFailure = createAction(
  getActionName(
    'Start communications notifications failure',
    communicationsActionPrefix,
  ),
  props<{ error: string }>(),
);

export const requestCustomClaimsSms = createAction(
  getActionName('Request Custom Claims SMSs', communicationsActionPrefix),
  props<{ request: SendClaimMessageRequest }>(),
);
export const requestCustomClaimsSmsSuccess = createAction(
  getActionName(
    'Request Custom Claims SMSs success',
    communicationsActionPrefix,
  ),
);
export const requestCustomClaimsSmsFailure = createAction(
  getActionName(
    'Request Custom Claims SMSs failure',
    communicationsActionPrefix,
  ),
  props<{ error: string }>(),
);

export const getPendingCustomClaimsSms = createAction(
  getActionName('Get pending Custom Claims SMS', communicationsActionPrefix),
  props<{ claimCode: string; claimContactId: number; requestId: number }>(),
);
export const getPendingCustomClaimsSmsSuccess = createAction(
  getActionName(
    'Get pending Custom Claims SMS success',
    communicationsActionPrefix,
  ),
  props<{ response: PendingCustomClaimSms }>(),
);
export const getPendingCustomClaimsSmsFailure = createAction(
  getActionName(
    'Get pending Custom Claims SMS failure',
    communicationsActionPrefix,
  ),
  props<{ error: string }>(),
);

export const actionPendingCustomClaimsSms = createAction(
  getActionName('Action pending Custom Claims SMS', communicationsActionPrefix),
  props<{ request: ActionCustomClaimSms }>(),
);
export const actionPendingCustomClaimsSmsSuccess = createAction(
  getActionName(
    'Action pending Custom Claims SMS success',
    communicationsActionPrefix,
  ),
);
export const actionPendingCustomClaimsSmsFailure = createAction(
  getActionName(
    'Action pending Custom Claims SMS failure',
    communicationsActionPrefix,
  ),
  props<{ error: string }>(),
);

export const customClaimSmsRequestsTableActions =
  new GenericTableActionsWithoutPropClass<
    CustomClaimSmsResponse,
    CommunicationsState
  >(
    communicationsActionPrefix,
    CommunicationTables.CustomClaimSmsRequests,
    getCommunicationsFeatureState,
  );
